import React, { useState } from 'react';
import { Box, SxProps, Typography, useTheme } from '@mui/material';
import { makeTransition } from 'shared/lib/utils/utils';
import { IconProps } from 'shared/icons/iconProps';

export default function ActionInlineButton({
  Icon,
  children,
  onClick,
  sx,
  disabled,
  defaultIconColor,
  hoverIconColor,
  sizeVariant = 'medium',
}: {
  Icon: (props: IconProps) => JSX.Element;
  children?: React.ReactNode;
  onClick: () => void;
  sx?: SxProps;
  disabled?: boolean;
  defaultIconColor?: string;
  hoverIconColor?: string;
  sizeVariant?: 'medium' | 'small';
}) {
  const theme = useTheme();
  const [hover, setHover] = useState(false);

  defaultIconColor = defaultIconColor || theme.palette.buttonsTonalInverse.main;
  hoverIconColor = hoverIconColor || theme.palette.buttonsTonalInverse.dark;

  const color = disabled
    ? theme.palette.text.secondary
    : hover
    ? theme.palette.buttonsTonalInverse.dark
    : theme.palette.buttonsTonalInverse.main;
  const iconColor = disabled
    ? theme.palette.text.secondary
    : hover
    ? hoverIconColor
    : defaultIconColor;

  const sizeKey = sizeVariant === 'medium' ? '18x18' : '16x16';
  const variant = sizeVariant === 'medium' ? 'f18' : 'f14';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        cursor: 'pointer',
        color,
        pointerEvents: disabled ? 'none' : 'all',
        opacity: disabled ? 0.4 : 1,
        width: 'fit-content',
        transition: makeTransition(['color']),
        ...sx,
      }}
      onMouseEnter={() => {
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
    >
      <Icon
        sizeKey={sizeKey}
        style={{ transition: makeTransition(['fill'], { duration: '125ms' }) }}
        extColor={iconColor}
      ></Icon>
      <Typography sx={{ fontWeight: 'bold' }} color="inherit" variant={variant}>
        {children}
      </Typography>
    </Box>
  );
}
